/* ============================================================================
   PANELVERSE — shared theme JS (vanilla, no dependencies).
   (a) TH/EN language toggle    — localStorage key "ch-lang"
   (b) generic countdowns       — [data-countdown-to], per-element persistence
   (c) header basket count      — /cart.js -> [data-cart-count]
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     (a) TH/EN language toggle
     - persists to localStorage key "ch-lang"
     - sets body class .is-th / .is-en (CSS hides [data-lang]/.lang-* opposites)
     - any element with [data-lang-toggle] switches language on click
  ------------------------------------------------------------------------ */
  var LANG_KEY = 'ch-lang';

  function storedLang() {
    try {
      var v = localStorage.getItem(LANG_KEY);
      if (v === 'th' || v === 'en') return v;
    } catch (e) { /* private mode etc. */ }
    return 'en'; // concept pages default to English copy
  }

  function applyLang(lang) {
    var body = document.body;
    body.classList.toggle('is-th', lang === 'th');
    body.classList.toggle('is-en', lang === 'en');
    document.documentElement.setAttribute('data-pv-lang', lang);
    var toggles = document.querySelectorAll('[data-lang-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-pressed', lang === 'th' ? 'true' : 'false');
    }
  }

  function setLang(lang) {
    if (lang !== 'th' && lang !== 'en') return;
    try { localStorage.setItem(LANG_KEY, lang); } catch (e) { /* noop */ }
    applyLang(lang);
  }

  function initLang() {
    applyLang(storedLang());
    document.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest ? ev.target.closest('[data-lang-toggle]') : null;
      if (!btn) return;
      ev.preventDefault();
      var explicit = btn.getAttribute('data-lang-toggle'); // "th" | "en" | "" (flip)
      var next = (explicit === 'th' || explicit === 'en')
        ? explicit
        : (storedLang() === 'th' ? 'en' : 'th');
      setLang(next);
    });
  }

  /* ------------------------------------------------------------------------
     (b) Generic countdown
     Any element with [data-countdown-to] ticks DD:HH:MM:SS every second.

     data-countdown-to accepts:
       - an absolute date/time parseable by Date.parse()
         e.g. data-countdown-to="2026-08-01T00:00:00+07:00"
       - a relative duration "9d 14h 42m" (any of d/h/m/s, in order) —
         the deadline is computed on first visit and persisted per element
         in localStorage key "pv-countdown-<element id>" (concept behavior).

     Display targets inside the element (all optional):
       [data-cd="dd"] [data-cd="hh"] [data-cd="mm"] [data-cd="ss"]
     If none exist, the element's text becomes "DD:HH:MM:SS" spans.
  ------------------------------------------------------------------------ */
  function parseDeadline(el, index) {
    var raw = (el.getAttribute('data-countdown-to') || '').trim();
    if (!raw) return NaN;

    var abs = Date.parse(raw);
    if (!isNaN(abs)) return abs;

    var m = raw.match(/^(?:(\d+)\s*d)?\s*(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?$/i);
    if (!m) return NaN;
    var secs = (Number(m[1]) || 0) * 86400 +
               (Number(m[2]) || 0) * 3600 +
               (Number(m[3]) || 0) * 60 +
               (Number(m[4]) || 0);
    if (secs <= 0) return NaN;

    var key = 'pv-countdown-' + (el.id || 'anon-' + index);
    var deadline = NaN;
    try {
      var saved = localStorage.getItem(key);
      if (saved && !isNaN(Number(saved))) {
        deadline = Number(saved);
        if (deadline <= Date.now()) { // expired: restart the window
          deadline = Date.now() + secs * 1000;
          localStorage.setItem(key, String(deadline));
        }
      } else {
        deadline = Date.now() + secs * 1000;
        localStorage.setItem(key, String(deadline));
      }
    } catch (e) {
      deadline = Date.now() + secs * 1000;
    }
    return deadline;
  }

  function countdownSpans(el) {
    var spans = {
      dd: el.querySelector('[data-cd="dd"]'),
      hh: el.querySelector('[data-cd="hh"]'),
      mm: el.querySelector('[data-cd="mm"]'),
      ss: el.querySelector('[data-cd="ss"]')
    };
    if (spans.dd || spans.hh || spans.mm || spans.ss) return spans;

    // no targets: build a plain DD:HH:MM:SS readout
    el.textContent = '';
    var units = ['dd', 'hh', 'mm', 'ss'];
    for (var i = 0; i < units.length; i++) {
      var s = document.createElement('span');
      s.setAttribute('data-cd', units[i]);
      s.textContent = '00';
      el.appendChild(s);
      if (i < units.length - 1) el.appendChild(document.createTextNode(':'));
    }
    return countdownSpans(el);
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  function initCountdowns() {
    var els = document.querySelectorAll('[data-countdown-to]');
    if (!els.length) return;

    var items = [];
    for (var i = 0; i < els.length; i++) {
      var deadline = parseDeadline(els[i], i);
      if (isNaN(deadline)) continue;
      items.push({ el: els[i], deadline: deadline, spans: countdownSpans(els[i]) });
    }
    if (!items.length) return;

    function tick() {
      var now = Date.now();
      for (var j = 0; j < items.length; j++) {
        var it = items[j];
        var left = Math.max(0, it.deadline - now) / 1000;
        var vals = {
          dd: pad(Math.floor(left / 86400)),
          hh: pad(Math.floor(left / 3600) % 24),
          mm: pad(Math.floor(left / 60) % 60),
          ss: pad(Math.floor(left) % 60)
        };
        for (var k in vals) {
          if (it.spans[k] && it.spans[k].textContent !== vals[k]) {
            it.spans[k].textContent = vals[k];
          }
        }
        if (left <= 0) it.el.setAttribute('data-countdown-expired', '');
      }
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------------
     (c) Basket count — refresh every [data-cart-count] badge from /cart.js
  ------------------------------------------------------------------------ */
  function refreshCartCount() {
    var badges = document.querySelectorAll('[data-cart-count]');
    if (!badges.length) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || typeof cart.item_count === 'undefined') return;
        for (var i = 0; i < badges.length; i++) {
          badges[i].textContent = cart.item_count;
        }
      })
      .catch(function () { /* offline / theme preview — keep Liquid-rendered count */ });
  }

  /* ------------------------------------------------------------------------
     init
  ------------------------------------------------------------------------ */
  function init() {
    initLang();
    initCountdowns();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // small public surface for section scripts
  window.Panelverse = window.Panelverse || {};
  window.Panelverse.setLang = setLang;
  window.Panelverse.refreshCartCount = refreshCartCount;
})();
